import { useState, useEffect, useRef } from 'react'
import { FiX, FiFolder, FiFolderPlus, FiCheck } from 'react-icons/fi'
import { toast } from 'sonner'
import { apiFetch } from '@/lib/api'
import { useShow } from '@/contexts/ShowContext'
import Button from '../ui/Button'
import { Input } from '../ui/shadcn/input'
import Loader from '../loader'

// Remember the last picked folder per folder-type so the picker re-opens where
// the user last confirmed. Keyed by show + type since folder trees are scoped to
// a show (and therefore an account); without the show in the key a folder id
// from a previously-active show/account would leak in and 404 on fetch.
const LAST_FOLDER_STORAGE_PREFIX = 'folderPicker:lastFolderId'

const storageKey = (showId, folderType) => `${LAST_FOLDER_STORAGE_PREFIX}:${showId || 'unknown'}:${folderType}`

const getStoredFolderId = (showId, folderType) => {
    if (typeof window === 'undefined') return null
    try {
        const value = localStorage.getItem(storageKey(showId, folderType))
        return value ? value : null
    } catch {
        return null
    }
}

const storeFolderId = (showId, folderType, folderId) => {
    if (typeof window === 'undefined') return
    try {
        const key = storageKey(showId, folderType)
        if (folderId) localStorage.setItem(key, folderId)
        else localStorage.removeItem(key)
    } catch {
        // localStorage might be unavailable
    }
}

// Folder picker. Resolves with `null` (cancel) or `{ folderId, folderName }`
// where folderId is `null` for root.
//
// Props:
// - excludeFolderIds: ids that are shown but greyed out and cannot be picked
//   or navigated into (e.g. folders the user is moving — descending into them
//   would let them pick a child as the destination, which is a cyclic move).
// - disabledFolderIds: folders whose ids appear here cannot be picked as a
//   destination. Use `null` in the array to disable picking root. Items already
//   living in these folders shouldn't be "moved" to the same folder, so we
//   disable confirm + the row itself.
// - allowNaming: when true, an asset-name field is shown and its value is
//   returned as `assetName` on confirm. Confirm is disabled while the name is
//   empty. `nameLabel`/`namePlaceholder`/`initialName` customise the field.
// - initialFolderId: pass an explicit starting folder. When omitted, the picker
//   restores the last confirmed folder from localStorage, falling back to root.
export default function FolderPickerModal({
    onClose,
    title = 'Choose Folder',
    initialFolderId,
    excludeFolderIds = [],
    disabledFolderIds = [],
    confirmLabel = 'Select',
    folderType = 'assets',
    allowCreate = true,
    allowNaming = false,
    nameLabel = 'Name',
    namePlaceholder = 'Asset name',
    initialName = '',
}) {
    const { show } = useShow()
    // Explicit prop wins; otherwise restore the last-used folder, then root.
    const [currentFolderId, setCurrentFolderId] = useState(
        () => (initialFolderId !== undefined ? initialFolderId : getStoredFolderId(show?._id, folderType)),
    )
    const [folders, setFolders] = useState([])
    const [ancestors, setAncestors] = useState([])
    const [loading, setLoading] = useState(false)
    const [creating, setCreating] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    const [submittingCreate, setSubmittingCreate] = useState(false)
    const [assetName, setAssetName] = useState(initialName)
    const newFolderInputRef = useRef(null)
    const assetNameInputRef = useRef(null)

    const excludeSet = new Set(excludeFolderIds.filter(Boolean))
    const isFolderExcluded = (folderId) => excludeSet.has(folderId)
    const isFolderDisabled = (folderId) => disabledFolderIds.includes(folderId ?? null)
    const nameMissing = allowNaming && !assetName.trim()
    const isCurrentDisabled = isFolderDisabled(currentFolderId) || isFolderExcluded(currentFolderId) || nameMissing

    const fetchFolders = async (folderId) => {
        try {
            setLoading(true)
            const url = `/api/shows/${show._id}/folders?folder=${folderId || ''}&type=${encodeURIComponent(folderType)}`
            const res = await apiFetch(url)
            if (!res.ok) {
                // A stored folder id from a previously-active show/account is no
                // longer reachable here (404/403). Drop the stale value and fall
                // back to root instead of leaving the picker stuck/erroring.
                if ((res.status === 404 || res.status === 403) && folderId) {
                    storeFolderId(show?._id, folderType, null)
                    setCurrentFolderId(null)
                    return
                }
                throw new Error('Failed to fetch folders')
            }
            const data = await res.json()
            setFolders(data.folders || [])
            setAncestors(data.ancestors || [])
        } catch (error) {
            console.error('Error loading folders:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchFolders(currentFolderId)
    }, [currentFolderId])

    useEffect(() => {
        if (creating) {
            // Focus the inline input as soon as it appears.
            requestAnimationFrame(() => newFolderInputRef.current?.focus())
        }
    }, [creating])

    useEffect(() => {
        if (allowNaming) {
            // Focus (and select) the name field as soon as the picker opens so
            // the user can immediately type/replace the suggested name.
            requestAnimationFrame(() => {
                assetNameInputRef.current?.focus()
                assetNameInputRef.current?.select()
            })
        }
    }, [allowNaming])

    const handleNavigate = (folder) => {
        if (excludeSet.has(folder._id)) return
        setCurrentFolderId(folder._id)
    }

    const handleConfirm = () => {
        if (isCurrentDisabled) return
        storeFolderId(show?._id, folderType, currentFolderId)
        const currentName = ancestors.length ? ancestors[ancestors.length - 1].name : 'Root'
        onClose({
            folderId: currentFolderId,
            folderName: currentName,
            ...(allowNaming ? { assetName: assetName.trim() } : {}),
        })
    }

    const startCreate = () => {
        setNewFolderName('')
        setCreating(true)
    }

    const cancelCreate = () => {
        setCreating(false)
        setNewFolderName('')
    }

    const submitCreate = async () => {
        const name = newFolderName.trim()
        if (!name || submittingCreate) return
        try {
            setSubmittingCreate(true)
            const res = await apiFetch(`/api/shows/${show._id}/folders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name,
                    parent: currentFolderId,
                    type: folderType,
                    visibility: 'public',
                }),
            })
            if (!res.ok) throw new Error('Failed to create folder')
            const created = await res.json()
            setCreating(false)
            setNewFolderName('')
            // Refresh the current folder listing so the new folder shows up
            // alongside any siblings, then surface it at the top so the user
            // can immediately pick or navigate into it.
            await fetchFolders(currentFolderId)
            setFolders((prev) => {
                const without = prev.filter((f) => f._id !== created._id)
                return [created, ...without]
            })
            toast.success('Folder created')
        } catch (error) {
            console.error(error)
            toast.error('Failed to create folder')
        } finally {
            setSubmittingCreate(false)
        }
    }

    const handleCreateKeyDown = (e) => {
        if (e.key === 'Enter') submitCreate()
        else if (e.key === 'Escape') {
            e.stopPropagation()
            cancelCreate()
        }
    }

    const visibleFolders = folders

    return (
        <div className='w-full max-w-[520px] h-[480px] rounded-md flex flex-col bg-background border'>
            <div className='flex justify-between py-2 px-4 border-b items-center'>
                <span className='text-lg font-bold'>{title}</span>
                <div className='cursor-pointer text-gray-400 hover:text-white' onClick={() => onClose(null)}>
                    <FiX className='w-5 h-5' />
                </div>
            </div>

            <div className='flex items-center gap-2 px-4 py-2 border-b text-xs text-secondary-foreground'>
                <div className='flex items-center gap-1 truncate flex-1'>
                    <span
                        className={`cursor-pointer hover:text-white ${!currentFolderId ? 'text-white' : ''}`}
                        onClick={() => setCurrentFolderId(null)}
                    >
                        Root
                    </span>
                    {ancestors.map((a, i) => (
                        <span key={a._id} className='flex items-center gap-1 truncate'>
                            <span>/</span>
                            <span
                                className={`cursor-pointer hover:text-white truncate ${i === ancestors.length - 1 ? 'text-white' : ''}`}
                                onClick={() => setCurrentFolderId(a._id)}
                            >
                                {a.name}
                            </span>
                        </span>
                    ))}
                </div>
                {allowCreate && !creating && (
                    <button
                        type='button'
                        className='flex items-center gap-1 text-xs text-secondary-foreground hover:text-white shrink-0'
                        onClick={startCreate}
                        title='Create a new folder here'
                    >
                        <FiFolderPlus className='w-4 h-4' />
                        <span>New Folder</span>
                    </button>
                )}
            </div>

            {allowNaming && (
                <div className='flex flex-col gap-1 px-4 py-3 border-b'>
                    <label className='text-[11px] uppercase tracking-wide text-secondary-foreground/60'>
                        {nameLabel}
                    </label>
                    <Input
                        ref={assetNameInputRef}
                        type='text'
                        placeholder={namePlaceholder}
                        value={assetName}
                        onChange={(e) => setAssetName(e.target.value)}
                        className='h-8 text-sm'
                    />
                </div>
            )}

            <div className='flex-1 overflow-y-auto p-2 relative'>
                {visibleFolders.length === 0 && !loading && !creating && (
                    <div className='flex items-center justify-center h-full text-secondary-foreground/60 text-sm'>
                        No subfolders
                    </div>
                )}
                <div className='flex flex-col'>
                    {creating && (
                        <div className='flex items-center gap-2 px-3 py-2 rounded-md bg-secondary/40'>
                            <FiFolder className='w-4 h-4 text-primary' />
                            <Input
                                ref={newFolderInputRef}
                                type='text'
                                placeholder='Folder name'
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                onKeyDown={handleCreateKeyDown}
                                disabled={submittingCreate}
                                className='h-7 text-sm flex-1'
                            />
                            <button
                                type='button'
                                className='p-1 rounded text-primary hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed'
                                onClick={submitCreate}
                                disabled={!newFolderName.trim() || submittingCreate}
                                title='Create folder'
                            >
                                <FiCheck className='w-4 h-4' />
                            </button>
                            <button
                                type='button'
                                className='p-1 rounded text-secondary-foreground hover:bg-secondary'
                                onClick={cancelCreate}
                                disabled={submittingCreate}
                                title='Cancel'
                            >
                                <FiX className='w-4 h-4' />
                            </button>
                        </div>
                    )}
                    {visibleFolders.map((folder) => {
                        const excluded = isFolderExcluded(folder._id)
                        const disabled = isFolderDisabled(folder._id)
                        const inactive = excluded || disabled
                        const tooltip = excluded
                            ? 'This folder is part of the selection being moved'
                            : disabled
                                ? 'Items are already in this folder'
                                : undefined
                        return (
                            <div
                                key={folder._id}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md ${inactive ? 'opacity-50 cursor-not-allowed' : 'hover:bg-secondary cursor-pointer'}`}
                                onDoubleClick={() => !inactive && handleNavigate(folder)}
                                onClick={() => !inactive && handleNavigate(folder)}
                                title={tooltip}
                            >
                                <FiFolder className='w-4 h-4 text-primary' />
                                <span className='text-sm flex-1 truncate'>{folder.name}</span>
                                {excluded && (
                                    <span className='text-[10px] bg-secondary text-secondary-foreground rounded-sm px-2 py-0.5'>Selected</span>
                                )}
                                {!excluded && disabled && (
                                    <span className='text-[10px] bg-secondary text-secondary-foreground rounded-sm px-2 py-0.5'>Current</span>
                                )}
                                {folder.visibility === 'private' && (
                                    <span className='text-[10px] bg-secondary text-secondary-foreground rounded-sm px-2 py-0.5'>Private</span>
                                )}
                            </div>
                        )
                    })}
                </div>
                {loading && <Loader />}
            </div>

            <div className='flex justify-between p-2 border-t'>
                <Button type='danger-ghost' className='min-w-20' onClick={() => onClose(null)}>Cancel</Button>
                <Button
                    type='primary'
                    className='min-w-20'
                    onClick={handleConfirm}
                    disabled={isCurrentDisabled}
                    title={nameMissing ? 'Enter a name' : isCurrentDisabled ? 'Items are already in this folder' : undefined}
                >{confirmLabel}</Button>
            </div>
        </div>
    )
}
